import { sharedAssets } from './assets/asset-store';
import { MusicStore } from './audio/music-store';
import { SoundStore } from './audio/sound-store';
import { HttpAccountClient } from './account/account-client';
import { HttpLoadoutClient } from './account/loadout-client';
import { HttpAuthClient } from './auth/http-auth-client';
import { SessionStore } from './auth/session-store';
import { SettingsStore } from './settings/settings-store';
import { config } from './config';
import { PHASE, profiler } from './debug/profiler';
import { installDevHandle } from './debug/dev-handle';
import { SERVER_RESTART_NOTICE_TEXT } from './game/constants/feel';
import { GameLoop } from './game/loop';
import { World } from './game/world';
import { PointerKeyboardSource } from './input/pointer-keyboard-source';
import { CompositeInputSource } from './input/intent';
import { TouchInputSource } from './input/touch-source';
import { UiIntentSource } from './input/ui-source';
import { checkServerBuild } from './net/build-check';
import { Connection, type ConnectionState } from './net/connection';
import { Outbound } from './net/outbound';
import { restartReloadDelayMs } from './net/restart-reload';
import { LoginStatus } from './protocol/gen/warinspace/v2/common_pb.js';
import { Camera, applyCameraPacket } from './render/camera';
import { Renderer } from './render/renderer';
import { mount } from 'svelte';
import Hud from './ui/Hud.svelte';
import TouchControls from './ui/hud/TouchControls.svelte';
import { HudState } from './ui/hud-state.svelte';
import Shell from './ui/Shell.svelte';
import { ShellController } from './ui/shell/shell-controller.svelte';
import { matchOverAnnouncement } from './ui/format';
import { settingsOverlay } from './ui/settings-open.svelte';
import {
  joinDeepLinkFromQuery,
  LOBBY_JOIN_QUERY_PARAM,
  LOBBY_SPECTATE_QUERY_PARAM,
} from './lobby/lobby-state';

function requireElement<T extends Element>(selector: string, expected: new () => T): T {
  const element: unknown = document.querySelector(selector);
  if (element === null) throw new Error(`Missing required element: ${selector}`);
  if (!(element instanceof expected)) {
    throw new Error(`Element ${selector} is not a ${expected.name}`);
  }
  return element;
}

const canvas = requireElement('#game', HTMLCanvasElement);

const world = new World();
const camera = new Camera();
const assets = sharedAssets;
const renderer = new Renderer(canvas, assets, { domBackground: true });
const sounds = new SoundStore();
const music = new MusicStore({ sounds });
const settings = new SettingsStore();
const sessions = new SessionStore();
const auth = new HttpAuthClient(config.apiBaseUrl);
const account = new HttpAccountClient(config.apiBaseUrl);
const loadouts = new HttpLoadoutClient(config.apiBaseUrl);

const uiInput = new UiIntentSource();
const touchInput = new TouchInputSource();
const input = new CompositeInputSource([
  uiInput,
  new PointerKeyboardSource(
    canvas,
    undefined,
    () => hudState.evolveScreenOpen || settingsOverlay.open || touchInput.driving,
  ),
  touchInput,
]);

const hudState = new HudState();

mount(TouchControls, {
  target: requireElement('#ui', HTMLDivElement),
  props: { touch: touchInput, hud: hudState },
});

mount(Hud, {
  target: requireElement('#ui', HTMLDivElement),
  props: {
    hud: hudState,
    world,
    assets,
    settings,
    onevolve: (path: number) => {
      uiInput.push({ kind: 'evolve', path });
    },
    onsurrender: (agree: boolean) => {
      uiInput.push({ kind: 'surrenderVote', agree });
    },
    onchat: (text: string) => {
      uiInput.push({ kind: 'sendChat', text });
    },
    onleave: () => {
      shellController.leaveLobby();
    },
    onseek: (x: number, y: number) => {
      uiInput.push({ kind: 'seekView', worldX: x, worldY: y });
    },
    onbuy: (slot: number) => {
      switch (slot) {
        case 0:
        case 1:
        case 2:
          uiInput.push({ kind: 'buyUpgrade', path: slot });
          break;
        default:
          uiInput.push({ kind: 'toggleUpgradeScreen' });
      }
    },
  },
});

const rememberedName = sessions.rememberedJoinName() ?? '';

const shellController = new ShellController(sessions, auth, account, loadouts);

/** The restart reload is the client's own, not the player leaving, so confirmLeave must not be able to strand them on a dead build. */
let reloadingForRestart = false;

const connection = new Connection({
  url: config.gameSocketUrl,

  sessionToken: () => sessions.current?.token ?? null,

  onAuthenticated: (authenticated) => {
    if (authenticated) {
      setStatus(`Connected as ${sessions.current?.username ?? 'your account'}`);
      return;
    }
    if (sessions.current !== null) {
      sessions.clear();
      shellController.refreshSession();
      shellController.loginError = 'Your session ended. Log in again to earn credits.';
      setStatus('Connected as a guest');
    }
  },

  onPacket: (packet) => {
    const now = performance.now();
    const prof = profiler.enabled ? profiler : null;
    prof?.begin(PHASE.net);
    world.apply(packet, now);
    prof?.end(PHASE.net);

    const payload = packet.payload;
    /** `world.dead` is normally copied into the camera once per frame; setting it here too avoids a stale value making the respawn snap use the dead zoom for ~20 frames. */
    camera.dead = world.dead;
    shellController.noteDeadState(world.dead);
    applyCameraPacket(camera, packet, world.selfShipId);

    if (payload.case === 'loginChange' && payload.value.status === LoginStatus.LOGGED_IN) {
      shellController.loginError = '';
    }

    if (payload.case === 'worldSetup') {
      shellController.roomReady();
    }

    if (payload.case === 'lobbyState') {
      const notice = shellController.noteLobbyState(payload.value);
      if (notice !== null) world.notify(notice, now);
    } else if (payload.case === 'lobbyClosed') {
      shellController.lobbyClosed(payload.value.reason);
    } else if (payload.case === 'notification') {
      shellController.noteNotification(payload.value.text, payload.value.subject);
    } else if (payload.case === 'serverBuild') {
      const verdict = checkServerBuild(
        payload.value.version,
        () => {
          reloadingForRestart = true;
          setStatus('Server updated. Reloading…');
          window.location.reload();
        },
        (message) => {
          world.notify(message, now);
        },
      );
      if (verdict === 'stale') return;
    } else if (payload.case === 'serverRestarting') {
      connection.noteServerRestarting();
      world.notify(SERVER_RESTART_NOTICE_TEXT, now);
    }

    if (payload.case === 'init') {
      world.selfName = shellController.lastJoinName;
      shellController.setVisible(false);
    } else if (payload.case === 'dead') {
      shellController.respawnAfterDeath();
      if (shellController.visible) world.exitMatch();
    }
  },

  onStateChange: (state: ConnectionState) => {
    switch (state) {
      case 'connecting':
        setStatus('Connecting…');
        shellController.setPlayable(false);
        world.resetAccount();
        break;
      case 'open':
        setStatus('Connected');
        shellController.setPlayable(true);
        break;
      case 'closed':
        setStatus(
          connection.isRestartPending
            ? 'Server updated. Reloading…'
            : 'Disconnected. Reconnecting…',
        );
        shellController.setPlayable(false);
        shellController.setVisible(true);
        if (connection.isRestartPending) {
          const delay = restartReloadDelayMs(
            performance.now(),
            world.matchInfo !== null,
            world.matchInfoAt,
          );
          reloadingForRestart = true;
          setTimeout(() => {
            window.location.reload();
          }, delay);
        }
        world.resetWorld();
        break;
      case 'idle':
        break;
    }
  },

  onDecodeError: (error, unreadBytes) => {
    console.warn(
      `Wire decode failed, discarded ${String(unreadBytes)} byte(s) of the frame:`,
      error.message,
    );
  },
});

function returnToMenu(): void {
  const info = world.matchInfo;
  if (info !== null) setStatus(matchOverAnnouncement(info, world.selfTeam));
  connection.send(Outbound.notPlayingNextRound());
  shellController.returnToMenu();
  world.exitMatch();
}

const loop = new GameLoop({
  world,
  connection,
  renderer,
  camera,
  input,
  onFrame: (now, fps) => {
    hudState.sync(world, camera, now, loop.spectatorCamera);
    hudState.fps = fps;
    if (shellController.visible) {
      world.dropSoundCues();
      return;
    }
    sounds.playCues(world.soundCues, {
      x: world.selfX,
      y: world.selfY,
      viewDist: camera.viewDist,
    });
  },
  onToggleUpgradeScreen: () => {
    hudState.toggleEvolveScreen();
  },
  onQuitToMenu: () => {
    if (world.matchInfo === null) {
      shellController.requestReturnToMenuOnDeath();
      return;
    }
    returnToMenu();
  },
  onMatchOver: returnToMenu,
  menuOpen: () => shellController.visible,
  idleMenu: () => settings.get('idleMenu') && !settingsOverlay.open,
  onError: (error) => {
    console.error('Game loop stopped:', error);
    setStatus('Something went wrong. Reload to try again.');
    shellController.setVisible(true);
  },
});

function setStatus(message: string): void {
  shellController.setStatus(message);
}

shellController.attachConnection(connection);

const deepLink = joinDeepLinkFromQuery(new URLSearchParams(window.location.search));
if (deepLink !== null) {
  shellController.queueJoinCode(deepLink, () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(LOBBY_JOIN_QUERY_PARAM);
    url.searchParams.delete(LOBBY_SPECTATE_QUERY_PARAM);
    window.history.replaceState(null, '', url);
  });
}

mount(Shell, {
  target: requireElement('#shell', HTMLDivElement),
  props: { shell: shellController, rememberedName, sounds, music, settings },
});

/** An AudioContext cannot start before a user gesture; cues queued before that are dropped. */
const startAudio = (): void => {
  sounds.resume();
  window.removeEventListener('pointerdown', startAudio);
  window.removeEventListener('keydown', startAudio);
};
window.addEventListener('pointerdown', startAudio);
window.addEventListener('keydown', startAudio);

void assets.loadFont();

renderer.resize(camera);
camera.snap();
connection.connect();
loop.start();

if (import.meta.env.DEV) {
  installDevHandle({ shell: shellController, world, camera, connection, hud: hudState, sounds });
}

/** Not `beforeunload`: that also fires for a leave the player then cancels, and would leave them looking at a dead game they chose to stay in. */
window.addEventListener('pagehide', () => {
  loop.stop();
  connection.dispose();
  input.dispose();
});

window.addEventListener('beforeunload', (event) => {
  if (reloadingForRestart) return;
  if (!settings.get('confirmLeave')) return;
  if (!hudState.playing && !hudState.dead) return;
  event.preventDefault();
});
